import React, { useState } from 'react';
import { OTPLoginProps } from './OTPLogin.types';
import { InputInfo } from 'sos-common/js/components/InputInfo/inputInfo';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';
import { checkWebApiAvailability, WebApiAvailability } from '../../utilities/checkWebApiAvailability';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { FileAPIClient } from '../FileAPIClient/FileApiClient';

import './otplogin.scss';
import { makeTranslator } from '../../utilities/translations';

export const OTPLogin: React.FC<OTPLoginProps> = (props) => {
    const OTP_LENGTH = 8;

    const [otp, setOtp] = useState<string>('');
    const [webApisAvailable, setWebApisAvailable] = React.useState<boolean>(false);
    const [displaySendOtpSection, setDisplaySendOtpSection] = useState<boolean>(true);
    const [displayOtpInClearText, setDisplayOtpInClearText] = useState<boolean>(false);
    const [isLoginButtonEnabled, setIsLoginButtonEnabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const Translate = makeTranslator(props.translations);

    React.useEffect(() => {
        async function checkWebApisAvailable() {
            const webApiAvailability: WebApiAvailability = await checkWebApiAvailability();

            setWebApisAvailable(
                webApiAvailability.isSecureContext &&
                webApiAvailability.isCryptoAvailable &&
                webApiAvailability.isGetRandomValuesAvailable &&
                webApiAvailability.isRandomUUIDAvailable &&
                webApiAvailability.isSubtleAvailable &&
                webApiAvailability.isShowFilePickerAvailable &&
                webApiAvailability.isFileSystemHandleAvailable &&
                webApiAvailability.isCreateWritableAvailable &&
                webApiAvailability.isBrowserSupported,
            );
        }

        checkWebApisAvailable();
    }, []);

    const handleOtpRequest = async () => {
        await requestOtp();
    };

    // Trigger för skickande av nytt Otp (one time password).
    const requestOtp = async () => {
        setIsLoading(true);
        try {
            let apiClient = new FileAPIClient();

            const response = await apiClient.getUserSession(undefined);

            if (response.success) {
                setDisplaySendOtpSection(false);
                displayInfoMessage(Translate('/otp/newcodehasbeensent'));
            } else {
                displayErrorMessage(response.code.toString());
            }
        } catch (error) {
            displayErrorMessage('/otp/resendcode');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async () => {
        const success = await login();
        if (success) props.onLoginSuccess();
    };

    // Login med inmatat Otp (one time password) för att skapa en AnvändarSession på serven.
    const login = async (): Promise<boolean> => {
        setIsLoading(true);
        try {
            let apiClient = new FileAPIClient();

            const response = await apiClient.getUserSession(otp);

            if (response.success && response.data) {
                if (response.data.isAuthenticated) {
                    return true;
                } else {
                    displayErrorMessage(Translate('/otP/failedretry'));
                    return false;
                }
            } else {
                displayErrorMessage(Translate('/otp/technicalerror'));
                return false;
            }
        } catch (error) {
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const displayInfoMessage = (message?: string) => {
        if (message) {
            setInfoMessage(message);
        }

        setErrorMessage('');
    };

    const displayErrorMessage = (message?: string) => {
        if (message) {
            setErrorMessage(message);
        }

        setInfoMessage('');
    };

    // Hanterar inmatat OTP värde
    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const currentOtpValue = e.target.value.slice(0, OTP_LENGTH);

        // Rensa information- och felfält när användaren börjar mata in otp
        if (currentOtpValue.length === 1) {
            setInfoMessage('');
            setErrorMessage('');
        }

        // Sätt OTP om det är ett nummer
        if (!isNaN(Number(currentOtpValue))) {
            setOtp(currentOtpValue);
        }

        // Visa Login knappen enbart när OTP har rätt längd
        setIsLoginButtonEnabled(currentOtpValue.length === OTP_LENGTH);
    };

    // Sektion för att begära OTP
    const renderRequestOtpSection = () => (
        <>
            <p>{Translate('/otp/message')}</p>
            <button
                className={`${isLoading ? 'otplogin__button--disabled' : ''} button m-b-2 otplogin__button`}
                disabled={isLoading}
                aria-disabled={isLoading}
                onClick={requestOtp}>
                <span>{Translate('/otp/sendcode')}</span>{' '}
                {isLoading && <LoadingSpinner darkColor={false} cssClasses="fileupload__spinner otplogin__spinner" />}
            </button>
        </>
    );

    // Sektion för att mata in och begära validering av OTP
    const renderOtpInputSection = () => (
        <>
            <div>
                <label htmlFor="otpPassword" aria-label={Translate('/otp/entercode')}>
                    {Translate('/otp/verificationcode')}
                </label>
                <input
                    id="otpPassword"
                    type={displayOtpInClearText ? 'text' : 'password'}
                    maxLength={OTP_LENGTH}
                    value={otp}
                    onChange={handleOtpChange}
                    onKeyDown={(event) => {
                        if (isLoginButtonEnabled && event.key === 'Enter') handleLogin();
                    }}
                    autoFocus
                />
                <p className="sr-only" aria-live="polite">
                    {(!isLoginButtonEnabled || isLoading) && Translate('/otp/verificationcode')}
                    {isLoginButtonEnabled && Translate('/otp/pressentertologin')}
                </p>
                <div aria-live="assertive">
                    {errorMessage && <InputError errorText={errorMessage} />}
                    {infoMessage && <InputInfo infoText={infoMessage} />}
                </div>
            </div>
            <div className="m-t-1">
                <button
                    id="loginbutton"
                    className={`${isLoading ? 'otplogin__button--disabled' : ''} button m-b-2 otplogin__button`}
                    aria-disabled={!isLoginButtonEnabled || isLoading}
                    disabled={!isLoginButtonEnabled || isLoading}
                    onClick={handleLogin}>
                    <span>{Translate('/otp/login')}</span>{' '}
                    {isLoading && <LoadingSpinner darkColor={false} cssClasses="fileupload__spinner otplogin__spinner" />}
                </button>
            </div>
            <div>
                <p>
                    {!isLoading && (
                        <a href="#" onClick={handleOtpRequest}>
                            {Translate('/otp/resendcode')}
                        </a>
                    )}
                </p>
            </div>
        </>
    );

    return (
        <div>
            {webApisAvailable ? (
                displaySendOtpSection ? (
                    renderRequestOtpSection()
                ) : (
                    renderOtpInputSection()
                )
            ) : (
                <div>
                    <p>{Translate('/otp/browsernotsupported')}.</p>

                    <p>{Translate('/otp/recommendedbrowsers')}:</p>
                    <ul>
                        <li>
                            <a href="https://www.google.com/chrome/" target="_blank" rel="noopener">
                                Google Chrome
                            </a>
                            .
                        </li>
                        <li>
                            <a href="https://www.microsoft.com/windows/microsoft-edge" target="_blank" rel="noopener">
                                Microsoft Edge
                            </a>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default OTPLogin;
